import React, { useContext, useEffect, useState } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import {
  AttributeWindowLinkModel,
  AttributeWindowLinkTypeModel,
  DrawingTypeModel,
} from "../api/drawingApi";
import { Problem } from "../api/backendApi";
import PictureLinkEdit from "./PictureLinkEdit";
import PDFLinkEdit from "./PDFLinkEdit";
import { DrawingContext } from "./DrawingContext";
import { PlusCircleFill, XCircle } from "react-bootstrap-icons";

interface AttributeWindowLinkEditProps {
  otherWindows: string[];
  links: AttributeWindowLinkModel[];
  readOnly: boolean;
  onChange: (links: AttributeWindowLinkModel[]) => void;
  onError: (error: Problem | string | undefined) => void;
}

function AttributeWindowLinkEdit({
  otherWindows,
  links,
  readOnly,
  onChange,
  onError,
}: AttributeWindowLinkEditProps) {
  const drawing = useContext(DrawingContext);
  const [selectedLinkTargetType, setSelectedLinkTargetType] =
    useState<AttributeWindowLinkTypeModel>(
      drawing.type === DrawingTypeModel.Template
        ? AttributeWindowLinkTypeModel.Link
        : AttributeWindowLinkTypeModel.Picture,
    );
  const [selectedLinkTarget, setSelectedLinkTarget] = useState<string>();
  const [formError, setFormError] = useState<string>();
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  useEffect(() => {
    setFormError(undefined);
    setSelectedLinkTarget(otherWindows.length > 0 ? otherWindows[0] : "");
  }, [otherWindows]);

  const handleDeleteButtonClick = (index: number) => {
    const modifiedLinks = links.filter((l, i) => i !== index);
    onChange(modifiedLinks);
  };

  const handleAddButtonClick = () => {
    if (!selectedLinkTarget) {
      setFormError("No option was selected");
      return;
    }

    setFormError(undefined);
    const modifiedLinks = [
      ...links,
      { targetType: selectedLinkTargetType, targetId: selectedLinkTarget },
    ];
    onChange(modifiedLinks);
  };

  const handleSelectedLinkTargetChange = (value: string) => {
    const option = parseInt(value, 10);
    if (option) {
      setSelectedLinkTargetType(option);
      setSelectedLinkTarget("");
    }
  };

  const handleDragStart = (index: any) => {
    setDraggedItemIndex(index);
  }

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
  }

  const handleDrop = (index: number) => {
    const updatedItems = [...links];
    const [draggedItem] = updatedItems.splice(draggedItemIndex ?? 0, 1);

    updatedItems.splice(index, 0, draggedItem);
    onChange(updatedItems)
    setDraggedItemIndex(null);
  }

  return (
    <>
      <Form.Group>
        <div style={{ minHeight: "calc(1.5em * 7)" }}>
          <Form.Label>Attribute window buttons</Form.Label>
          <Table size="sm">
            <tbody>
              {links.length === 0 && (
                <tr>
                  <td colSpan={3}>{"<none>"}</td>
                </tr>
              )}
              {links.map((l, index) => (
                <tr key={index}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index)}
                style={{
                  cursor: "grab",
                }}
                >
                  <td>{AttributeWindowLinkTypeModel[l.targetType]}</td>
                  <td>{l.targetId}</td>
                  {!readOnly && (
                    <td
                      className={readOnly ? "d-none" : undefined}
                      style={{ width: "1px" }}
                    >
                      <XCircle
                        role="button"
                        color="red"
                        title="Remove"
                        onClick={() => handleDeleteButtonClick(index)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        {!readOnly && links.length < 4 && (
          <div>
            <Form.Label>Add new button: </Form.Label>
            <Row className="g-1">
              <Col md="auto">
                <Form.Select
                  size="sm"
                  defaultValue={selectedLinkTargetType}
                  onChange={(e) =>
                    handleSelectedLinkTargetChange(e.target.value)
                  }
                >
                  <option value="1">Link</option>
                  {drawing.type === DrawingTypeModel.Drawing && (
                    <>
                      <option value="2">Picture</option>
                      <option value="3">External PDF</option>
                    </>
                  )}
                </Form.Select>
              </Col>
              {selectedLinkTargetType === AttributeWindowLinkTypeModel.Link && (
                <Col>
                  <Form.Select
                    defaultValue={selectedLinkTarget}
                    size="sm"
                    onChange={(e) => setSelectedLinkTarget(e.target.value)}
                  >
                    {otherWindows &&
                      otherWindows
                        .filter(
                          (ow) =>
                            links.findIndex(
                              (l) =>
                                l.targetType ===
                                  AttributeWindowLinkTypeModel.Link &&
                                l.targetId === ow,
                            ) === -1,
                        )
                        .map((o) => (
                          <option value={o} key={o}>
                            {o}
                          </option>
                        ))}
                  </Form.Select>
                </Col>
              )}
              {selectedLinkTargetType ===
                AttributeWindowLinkTypeModel.Picture && (
                <PictureLinkEdit
                  vehicleFileNumber={drawing.name}
                  onChange={(p) => setSelectedLinkTarget(p)}
                  onError={(e) => onError(e)}
                />
              )}
              {selectedLinkTargetType === AttributeWindowLinkTypeModel.PDF && (
                <PDFLinkEdit
                  vehicleFileNumber={drawing.name}
                  onChange={(p) => setSelectedLinkTarget(p)}
                  onError={(e) => onError(e)}
                />
              )}
              <Col md="auto">
                <PlusCircleFill
                  className="mx-1"
                  role="button"
                  title="Add"
                  color="green"
                  onClick={() => handleAddButtonClick()}
                  size="1.5em"
                />
              </Col>
            </Row>
            {formError && <div style={{ color: "red" }}>{formError}</div>}
          </div>
        )}
      </Form.Group>
    </>
  );
}

export default AttributeWindowLinkEdit;
